import { useState, useEffect, useRef } from "react";
import { Redirect } from "react-router-dom";
import { useTunnelData } from "../TunnelContext";
import Api from "../baseApi";
import Breadcrumbs from "../components/Breadcrumbs";
import OAButton from "../components/OAButton";
import { ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom/cjs/react-router-dom";
import { useTranslation } from "react-i18next";
import { Validators } from "../lib/validators";
import { CircleAlert } from "lucide-react";
import OAInput from "../components/input/OAInput";
import OAAddress from "../components/input/OAAddress";
import OAPhoneInput from "../components/input/OAPhoneInput";
import { Calendar } from "primereact/calendar";
import LoadingScreen from "../components/LoadingScreen";
import { addLocale } from "primereact/api";
import pushEvent from "../pushEvent";
import { useConfig } from "../ConfigProvider";
import Footer from "../components/Footer";
import { getFrenchCodeAndCoutryNameFromIso3166 } from "../lib/countries";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { cn } from "../lib/utils";

function getLeadSource(tunnel) {
  if (tunnel.utm_source === "CHATBOT_IG") {
    return "CHATBOT_IG";
  }

  return "MSDS_R2_VEL";
}

export default function UserFormSpecific() {
  return <UserForm />;
}

function UserForm({ offer }) {
  const { tunnel, updateTunnelUser, updateTunnelUserAddress, updateTunnelContactResa, createSale } = useTunnelData();
  const config = useConfig();
  const [step, setStep] = useState("FORM_1");
  const [errorMsg, errorMsgSet] = useState(false);

  const mql = window.matchMedia("(max-width: 600px)");
  const isMobile = mql.matches;

  const [errors, setErrors] = useState({});
  const { t, i18n } = useTranslation();

  const autocompleteRef = useRef();

  const { placesService, placePredictions, getPlacePredictions, isPlacePredictionsLoading } = usePlacesService({
    apiKey: "AIzaSyAFPrGdjW4E9zth5knmPfVhwPyaNNDhn1Y",
    options: {
      types: ["street_address"],
      fields: ["address_components", "formatted_address", "types", "icon", "name"],
    },
    language: "ES",
  });

  useEffect(() => {
    // fetch

    if (placePredictions[0]) {
      placesService?.getDetails(
        {
          placeId: placePredictions[0].place_id,
        },
        (placeDetails) => {
          let street_number = findInGooglePlace(placeDetails, "street_number");
          const route = findInGooglePlace(placeDetails, "route");
          let postal_code = findInGooglePlace(placeDetails, "postal_code");
          let locality = findInGooglePlace(placeDetails, "locality");
          const gCountryCode = findInGooglePlace(placeDetails, "country", "short_name");
          const [countryCode, country] = getFrenchCodeAndCoutryNameFromIso3166(gCountryCode);

          const update = {
            addressFull: placeDetails.formatted_address,
            address: [street_number, route].filter((e) => e).join(" "),
            address2: "",
            zipcode: postal_code,
            city: locality,
            country: country,
            countryCode: countryCode,
          };

          updateTunnelUserAddress(update);
          autocompleteRef.current.value = placeDetails.formatted_address;
        }
      );
    }
  }, [placePredictions]);

  addLocale("fr", {
    firstDayOfWeek: 1,
    dayNames: ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"],
    dayNamesShort: ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"],
    dayNamesMin: ["D", "L", "M", "M", "J", "V", "S"],
    monthNames: [
      "Janvier ",
      "Février ",
      "Mars ",
      "Avril ",
      "Mai ",
      "Juin ",
      "Juillet ",
      "Août ",
      "Septembre ",
      "Octobre ",
      "Novembre ",
      "Décembre ",
    ],
    monthNamesShort: ["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Août", "Sep", "Oct", "Nov", "Déc"],
    today: "Aujourd'hui",
    clear: "Réinitialiser",
  });

  addLocale("es", {
    firstDayOfWeek: 1,
    dayNames: ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"],
    dayNamesShort: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"],
    dayNamesMin: ["D", "L", "M", "X", "J", "V", "S"],
    monthNames: [
      "Enero ",
      "Febrero ",
      "Marzo ",
      "Abril ",
      "Mayo ",
      "Junio ",
      "Julio ",
      "Agosto ",
      "Septiembre ",
      "Octubre ",
      "Noviembre ",
      "Diciembre ",
    ],
    monthNamesShort: ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"],
    today: "Hoy",
    clear: "Limpiar",
  });

  useEffect(() => {
    tunnel.offer &&
      pushEvent({
        event: "funnel_step_3",
        step_name: "Abonnement",
        funnel_club: tunnel.club.name,
        funnel_club_status: "Ouvert",
        funnel_abo: tunnel.offer.offer.name,
      });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!tunnel.club) return <Redirect to={"/"} />;

  const findInGooglePlace = (place, type, field = "long_name") => {
    const compo = place.address_components.find((c) => c.types.indexOf(type) !== -1);
    return compo?.[field];
  };

  const addressSelected = (googlePlace) => {
    if (!googlePlace) {
      const update = {
        addressFull: "",
        address: "",
        address2: "",
        zipcode: "",
        city: "",
        country: "",
      };
      updateTunnelUserAddress(update);
      setError("address", null);
      return;
    }

    let street_number = findInGooglePlace(googlePlace, "street_number");
    const route = findInGooglePlace(googlePlace, "route");
    let postal_code = findInGooglePlace(googlePlace, "postal_code");
    let locality = findInGooglePlace(googlePlace, "locality");
    const gCountryCode = findInGooglePlace(googlePlace, "country", "short_name");
    const gCountry = findInGooglePlace(googlePlace, "country");
    const [countryCode, country] = getFrenchCodeAndCoutryNameFromIso3166(gCountryCode);

    if (!street_number && route && config.code === "onair.es" && gCountryCode === "ES") {
      const update = {
        addressFull: googlePlace.formatted_address,
        address: [street_number, route].filter((e) => e).join(" "),
        address2: "",
        zipcode: postal_code,
        city: locality,
        country: country,
        countryCode: countryCode,
      };

      updateTunnelUserAddress(update);

      getPlacePredictions({
        input: [route, 1, locality, gCountry].filter((e) => e).join(", "),
      });
      return;
    }

    if (!postal_code && gCountryCode === "MA") {
      postal_code = "20000";
      locality = "Casablanca";
    }

    const update = {
      addressFull: googlePlace.formatted_address,
      address: [street_number, route].filter((e) => e).join(" "),
      address2: "",
      zipcode: postal_code,
      city: locality,
      country: country,
      countryCode: countryCode,
    };

    updateTunnelUserAddress(update);
  };

  const getFirstLocalityInFranceOrSpain = (data) => {
    const results = data.results;

    const isInCountry = (component, countryName) =>
      component.types.includes("country") && component.long_name === countryName;

    let firstLocalityInFrance = null;
    let firstLocalityInSpain = null;

    for (let result of results) {
      let locality = null;
      let isFrance = false;
      let isSpain = false;

      for (let component of result.address_components) {
        if (component.types.includes("locality")) locality = component.long_name;
        if (isInCountry(component, "France")) isFrance = true;
        if (isInCountry(component, "Spain")) isSpain = true;
      }

      if (locality) {
        if (isFrance && !firstLocalityInFrance) {
          firstLocalityInFrance = locality;
        }
        if (isSpain && !firstLocalityInSpain) {
          firstLocalityInSpain = locality;
        }
      }

      if (firstLocalityInFrance) break;
    }

    return firstLocalityInFrance || firstLocalityInSpain || null;
  };

  const setError = (field, error) => {
    if (error) setErrors((prev) => ({ ...prev, [field]: error }));
    else
      setErrors((prev) => {
        let err = prev;
        delete err[field];
        return { ...err };
      });
  };

  const onChange = (e) => {
    const { name, value } = e.target;

    if (name === "optin") {
      const update = { optin: !tunnel.user?.optin };
      updateTunnelUser(update);
    } else {
      const update = { [name]: value };
      updateTunnelUser(update);
    }

    let validation = {};
    switch (name) {
      case "email":
        validation = Validators.validateEmail(tunnel.user?.email);
        if (!validation.ok) setError(name, validation.error);
        else setError(name, null);
        break;
      case "confirmEmail":
        validation = Validators.validateConfirmEmail(tunnel.user?.email, tunnel.user?.confirmEmail);
        if (!validation.ok) setError(name, validation.error);
        else setError(name, null);
        break;
      case "phone":
        validation = Validators.validatePhoneNumber(tunnel.user?.phone);
        if (!validation.ok) setError(name, validation.error);
        else setError(name, null);
        break;
      case "lastName":
        validation = Validators.validateName(tunnel.user?.lastName);
        if (!validation.ok) setError(name, validation.error);
        else setError(name, null);
        break;
      case "firstName":
        validation = Validators.validateName(tunnel.user?.firstName);
        if (!validation.ok) setError(name, validation.error);
        else setError(name, null);
        break;
      case "honeypot":
        if (tunnel.user?.honeypot) setError(name, "catched");
        break;
      case "birthdate":
        validation = Validators.validateBirthdate(tunnel.user?.birthdate);
        if (!validation.ok) setError(name, validation.error);
        else setError(name, null);
        break;
      case "acceptTerms":
        validation = { ok: tunnel.user.acceptTerms, error: "error" };
        if (!validation.ok) setError(name, validation.error);
        else setError(name, null);
        break;
      default:
        validation = Validators.requiredField(tunnel.user[name]);
        if (!validation.ok) setError(name, validation.error);
        else setError(name, null);
        break;
    }
  };

  const validateAllFields = () => {
    const newErrors = {};
    const fieldsToValidate = [
      "gender",
      "lastName",
      "firstName",
      "email",
      "confirmEmail",
      "phone",
      "birthdate",
      "acceptTerms",
      "honeypot",
      "address",
    ];

    fieldsToValidate.forEach((field) => {
      let validation = {};
      let error = "";
      switch (field) {
        case "address":
          if (!tunnel.user?.addressFull) error = t("errors.street-adress.empty");

          if (!tunnel.user?.zipcode) error = t("errors.street-adress.no-zip-code");

          // Test - address does not start with a number
          // Removed for now, as it was causing issues with some addresses in Martinique
          /* if (!(tunnel.user.address && tunnel.user.address.match(/^\d/)))
            error = t("errors.street-adress.no-street-number"); */
          break;
        case "email":
          validation = Validators.validateEmail(tunnel.user?.email);
          if (!validation.ok) error = validation.error;
          else error = null;
          break;
        case "confirmEmail":
          validation = Validators.validateConfirmEmail(tunnel.user?.email, tunnel.user?.confirmEmail);
          if (!validation.ok) error = validation.error;
          else error = null;
          break;
        case "phone":
          validation = Validators.validatePhoneNumber(tunnel.user?.phone);
          if (!validation.ok) error = validation.error;
          else error = null;
          break;
        case "lastName":
          validation = Validators.validateName(tunnel.user?.lastName);
          if (!validation.ok) error = validation.error;
          else error = null;
          break;
        case "firstName":
          validation = Validators.validateName(tunnel.user?.firstName);
          if (!validation.ok) error = validation.error;
          else error = null;
          break;
        case "honeypot":
          if (tunnel.user?.honeypot) error = "caught";
          break;
        case "birthdate":
          validation = Validators.validateBirthdate(tunnel.user?.birthdate);
          if (!validation.ok) error = validation.error;
          else error = null;
          break;
        case "acceptTerms":
          validation = { ok: tunnel.user.acceptTerms, error: "error" };
          if (!validation.ok) error = validation.error;
          else error = null;
          break;
        default:
          validation = Validators.requiredField(tunnel.user[field]);
          if (!validation.ok) error = validation.error;
          else error = null;
          break;
      }
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return false;
    } else {
      return true;
    }
  };

  const doCreateSale = async (contact) => {
    const previousStep = step;
    errorMsgSet(false);
    setStep("CREATING_SALE");
    return createSale(contact)
      .then((ret) => {
        if (i18n.language === "fr") {
          setStep("SEPA");
        } else {
          setStep("CARD");
        }
        return true;
      })
      .catch((err) => {
        errorMsgSet(t(err.errorCode) || err.userMsg || t("error_unknow"));
        setStep(previousStep);
        return false;
      });
  };

  const dateToDMY = (date) => {
    const tmpDate = new Date(date);
    const year = tmpDate.getFullYear();
    const month = ("0" + (tmpDate.getMonth() + 1)).slice(-2);
    const day = ("0" + tmpDate.getDate()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validateAllFields()) {
      return;
    }

    setStep("LOADING");
    errorMsgSet(null);
    setErrors([]);

    if (tunnel.contactResa && !tunnel.user.hasChanged) {
      return doCreateSale();
    }

    const [leadQuery] = Api.POST({
      path: "/api/abo/lead",
      body: {
        place: {
          id: tunnel.club.msdsId,
        },
        client: {
          firstname: tunnel.user.firstName,
          lastname: tunnel.user.lastName,
          email: tunnel.user.email,
          phone: tunnel.user.phone,
        },
        source: getLeadSource(tunnel),
        source_url: window.location.href,
      },
    });

    try {
      await leadQuery;
    } catch (err) {
      console.error(err);
    }

    const apiBody = {
      tunnelUid: tunnel.uid,
      trackerUid: tunnel.trackerUid,
      utm_source: tunnel.utm_source,
      utm_campaign: tunnel.utm_campaign,
      utm_medium: tunnel.utm_medium,
      msdsId: tunnel.club.msdsId,
      user: {
        gender: tunnel.user.gender,
        lastname: tunnel.user.lastName,
        firstname: tunnel.user.firstName,
        phone: tunnel.user.phone,
        email: tunnel.user.email,
        address: {
          streetAddress: `${tunnel.user.address} ${!!tunnel.user.address2 ? tunnel.user.address2 : ""}`.trim(),
          postcode: tunnel.user.zipcode,
          city: tunnel.user.city,
          country: tunnel.user.country,
        },
        birthdate: dateToDMY(tunnel.user.birthdate),
      },
    };

    const [query] = Api.POST({
      path: "/api/abo/resamania2/contact",
      body: apiBody,
    });
    query
      .then(({ contact }) => {
        updateTunnelContactResa(contact);
        try {
          localStorage.setItem("user", JSON.stringify(tunnel.user));
        } catch (e) {
          //Don't care
        }
        console.debug("doCreateSale with", contact);
        return doCreateSale(contact).then(() => {
          console.log("doCreateSale has this contactResa", tunnel.contactResa);
        });
      })
      .catch((err) => {
        setStep("FORM_1");
        errorMsgSet(err.userMsg || t("error_unknow"));
        console.error(err);
      });
  };

  if (step === "NEXT") return <Redirect push to={"/plans"} />;

  if (step === "LOADING") {
    return <LoadingScreen LoadingText={t("loading_accountCreating")} />;
  }

  if (step === "CREATING_SALE") {
    return <LoadingScreen LoadingText={t("loading_cartCreating")} />;
  }

  if (step === "SEPA") {
    return <Redirect push to={"/sepa"} />;
  }
  if (step === "CARD") {
    return <Redirect push to={"/card"} />;
  }

  if (step === "FORM_1") {
    return (
      <>
        <main className="bg-oaBackgroundColor min-h-screen animate-fade-in-left w-full flex-row sm:flex sm:flex-col xl:flex-grow xl:justify-center sm:items-center lg:px-20 xl:gap-16">
          <Link
            to="/"
            className="absolute left-4 top-5 md:left-20 flex flex-row hover:text-oaRed transition-all duration-300"
          >
            <ChevronLeft className="mr-2" />
            {t("previous_step")}
          </Link>
          <section className="w-full xl:w-2/3 h-full px-5 py-4 md:px-20 xl:px-5">
            <div className="w-full animate-fade-in-left">
              <Breadcrumbs />
            </div>

            <div>
              <h2
                className={cn(
                  "text-4xl font-bold text-oaForegroundText text-nowrap hidden xl:block text-center",
                  tunnel.offer?.offer?.validThrough ? "my-10" : "my-20"
                )}
              >
                {t("user_information.title").toUpperCase()}
              </h2>

              <form onSubmit={submit} className="flex flex-col gap-3 xl:px-24">
                <div>
                  <h3 className="text-2xl xl:font-extrabold">{t("user_information.form1.title").toUpperCase()}</h3>
                  <div className="flex flex-col gap-3 mt-3">
                    <div className="flex items-center gap-2">
                      <select
                        className={`w-40 p-2  bg-white border-oaBackgroundText border rounded-[8px] ${
                          errors.gender ? "border-2 border-oaRed" : ""
                        }`}
                        name="gender"
                        value={tunnel.user?.gender || ""}
                        onChange={onChange}
                        onBlur={onChange}
                        required
                      >
                        <option value="">{t("user_information.form1.civility.civility")}</option>
                        <option value="M">{t("user_information.form1.civility.sir")}</option>
                        <option value="F">{t("user_information.form1.civility.madam")}</option>
                      </select>
                      <div className="flex gap-1 justify-start items-start text-oaRed">
                        {errors.gender && (
                          <>
                            <CircleAlert className="size-4 " />
                            <p className="text-xs flex-1">{t(errors.gender)}</p>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col xl:flex-row gap-3">
                      <OAInput
                        placeholder={t("user_information.form1.placeholders.first_name")}
                        name={"firstName"}
                        value={tunnel.user?.firstName || ""}
                        onChange={onChange}
                        onBlur={onChange}
                        error={errors.firstName}
                      ></OAInput>
                      <OAInput
                        placeholder={t("user_information.form1.placeholders.last_name")}
                        name={"lastName"}
                        value={tunnel.user?.lastName || ""}
                        onChange={onChange}
                        onBlur={onChange}
                        error={errors.lastName}
                      ></OAInput>
                    </div>
                    <div className="flex flex-col xl:flex-row gap-3">
                      <fieldset className="relative animate-fade-in-down flex-1 flex flex-col gap-1">
                        {!isMobile && (
                          <Calendar
                            className={`w-full h-10 px-2 py-2 focus:outline-none bg-white border border-oaInputBorderColor rounded-[8px] ${
                              errors.birthdate ? "border-2 border-oaRed" : ""
                            }`}
                            value={tunnel.user?.birthdate}
                            onChange={onChange}
                            dateFormat="dd/mm/yy"
                            type="date"
                            name={"birthdate"}
                            placeholder={t("user_information.form1.placeholders.birth_date")}
                            showIcon
                            locale={i18n.language === "fr" ? "fr" : "es"}
                            mask="99/99/9999"
                            slotChar="dd/mm/yyyy"
                          />
                        )}
                        {isMobile && (
                          <>
                            <style>
                              {`
															input[type="date"]
															{
																display:block;
																// min-height: 1.2em;
																-webkit-appearance: none;
															}

															input[type='date']:before {
																color: #9ca3af;
																content: attr(placeholder);
																position: absolute;
															}

															input[type="date"].not-empty:before 
															{
																content:'';
															}

															input[type=date]::-webkit-datetime-edit-text {
																display: none;
															}

															input[type=date]::-webkit-datetime-edit-month-field{
																display: none;
															}

															input[type=date]::-webkit-datetime-edit-day-field {
																display: none;
															}

															input[type=date]::-webkit-datetime-edit-year-field {
																display: none;
															}

															input[type=date].not-empty::-webkit-datetime-edit-text {
																display: inline;
															}

															input[type=date].not-empty::-webkit-datetime-edit-month-field{
																display: inline;
															}

															input[type=date].not-empty::-webkit-datetime-edit-day-field {
																display: inline;
															}

															input[type=date].not-empty::-webkit-datetime-edit-year-field {
																display: inline;
															}
															`}
                            </style>
                            <input
                              type="date"
                              className="w-full h-10 px-2 py-3 border bg-white border-oaInputBorderColor rounded-[8px]"
                              name="birthdate"
                              placeholder={t("user_information.form1.placeholders.birth_date")}
                              onChange={(e) => {
                                if (e.currentTarget.value) {
                                  if (!e.currentTarget.classList.contains("not-empty")) {
                                    e.currentTarget.classList.add("not-empty");
                                  }
                                } else {
                                  if (e.currentTarget.classList.contains("not-empty")) {
                                    e.currentTarget.classList.remove("not-empty");
                                  }
                                }
                                onChange(e);
                              }}
                            />
                          </>
                        )}
                        <div className="flex gap-1 justify-start items-start text-oaRed">
                          {errors.birthdate && (
                            <>
                              <CircleAlert className="size-4 " />
                              <p className="text-xs flex-1">{t(errors.birthdate)}</p>
                            </>
                          )}
                        </div>
                      </fieldset>

                      <OAPhoneInput
                        placeholder={t("user_information.form1.placeholders.phone_number")}
                        name={"phone"}
                        phoneValue={tunnel.user?.phone}
                        onPhoneChange={onChange}
                        onBlur={onChange}
                        error={errors.phone}
                        defaultInternationnalCode={config.phone.defaultInternationnalCode}
                      />
                    </div>
                    <p className="text-[10px] xl:text-sm text-oaPlaceholderText">
                      {t("user_information.form1.placeholders.phone_number_info")}
                    </p>
                    <div className="flex flex-col xl:flex-row gap-3">
                      <OAInput
                        type="email"
                        placeholder={t("user_information.form1.placeholders.email")}
                        name={"email"}
                        value={tunnel.user?.email || ""}
                        onChange={onChange}
                        onBlur={onChange}
                        error={errors.email}
                      ></OAInput>
                      <OAInput
                        type="email"
                        placeholder={t("user_information.form1.placeholders.confirm_email")}
                        name={"confirmEmail"}
                        value={tunnel.user?.confirmEmail || ""}
                        onChange={onChange}
                        onBlur={onChange}
                        error={errors.confirmEmail}
                      ></OAInput>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl xl:font-extrabold">{t("user_information.form2.title").toUpperCase()}</h3>
                  <div className="flex flex-col mt-4 gap-3">
                    <div className="flex flex-col xl:flex-row gap-3 ">
                      <OAAddress
                        placeholder={tunnel.user?.addressFull || "..."}
                        addressSelected={addressSelected}
                        onChange={() => addressSelected(null)}
                        onBlur={onChange}
                        error={errors.address}
                        // value={tunnel.user?.addressFull}
                        autocompleteRef={autocompleteRef}
                      />
                    </div>
                  </div>
                </div>
                <div className="my-8 flex flex-col gap-1 xl:gap-2">
                  <div className="flex gap-1 items-start">
                    <input
                      type="checkbox"
                      name="oa-accept-data"
                      id="oa-accept-data"
                      className="mt-1"
                      onChange={(e) => onChange({ target: { name: "acceptDataShare", value: e.target.checked } })}
                    />
                    <label htmlFor="oa-accept-data" className="text-base text-oaBackgroundText p-0">
                      {t("user_information.checks.data_conditions")}
                    </label>
                  </div>
                  <div className="flex gap-1 items-start">
                    <input
                      type="checkbox"
                      name="acceptTerms"
                      id="oa-accept-terms"
                      className="mt-1 ring-1 ring-oaRed border-oaRed"
                      checked={tunnel?.user?.acceptTerms}
                      onChange={(e) => onChange({ target: { name: "acceptTerms", value: e.target.checked } })}
                    />
                    <label htmlFor="oa-accept-terms" className="text-base text-oaRed p-0">
                      {t("user_information.checks.terms_conditions")}
                    </label>
                  </div>
                  {errors.acceptTerms && (
                    <div className="text-center underline text-oaRed flex items-center justify-center gap-1">
                      <CircleAlert className="size-5 " />
                      <div>{t("errors.terms")}</div>
                    </div>
                  )}
                </div>
                <OAInput name={"honeypot"} onChange={onChange} type="hidden" />
              </form>
              {errorMsg && (
                <div className="text-center underline text-oaRed flex items-center justify-center gap-1">
                  <CircleAlert className="size-5 " />
                  <div>{errorMsg}</div>
                </div>
              )}
              {Object.keys(errors).length ? (
                <div className="text-center text-oaRed flex items-start justify-center gap-1">
                  <CircleAlert className="size-5" />
                  <div>{t("errors.required_fields")}</div>
                </div>
              ) : (
                ""
              )}
              <div className="w-full flex justify-center mt-4">
                <OAButton className={"w-56 h-10 lg:h-12 lg:w-64 xl:w-80 text-xl lg:text-2xl"} onClick={submit}>
                  {t("user_information.i_confirm").toUpperCase()}
                </OAButton>
              </div>
            </div>
          </section>
        </main>
        <footer>
          <Footer></Footer>
        </footer>
      </>
    );
  }
  return <p>INVALID STEP</p>;
}
